import { registerPushSubscription } from '@jq4race/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PushPermissionStatus = 'unsupported' | 'disabled' | 'default' | 'granted' | 'denied';

export interface UsePushNotificationsReturn {
  permissionStatus: PushPermissionStatus;
  requestPermission: () => Promise<void>;
}

// ── Helper: urlBase64ToUint8Array ─────────────────────────────────────────────
// Standard conversion for VAPID applicationServerKey.

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * usePushNotifications — manages Web Push permission and subscription lifecycle.
 *
 * Degradation contract:
 *   - No VAPID key → returns status 'disabled', no-op on requestPermission.
 *   - No serviceWorker or Notification API → returns 'unsupported'.
 *   - Permission 'denied' → returns 'denied', app continues without push.
 *   - Permission 'granted' → subscribes via pushManager and registers with backend.
 *
 * Idempotency: if pushManager.getSubscription() returns an existing subscription,
 * it is reused (re-registered with the backend) instead of creating a duplicate.
 */
export function usePushNotifications(): UsePushNotificationsReturn {
  // Read at render time so vi.stubEnv works in tests; value is stable in production.
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

  const [permissionStatus, setPermissionStatus] = useState<PushPermissionStatus>(() => {
    if (!vapidKey) return 'disabled';
    if (typeof window === 'undefined') return 'unsupported';
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return 'unsupported';
    return Notification.permission as PushPermissionStatus;
  });

  const subscribing = useRef(false);

  // Sync permission status on mount in case the browser already has a decision.
  useEffect(() => {
    if (!vapidKey) return;
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;

    const current = Notification.permission as PushPermissionStatus;
    setPermissionStatus(current);
  }, []);

  const subscribe = useCallback(async () => {
    if (!vapidKey) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.ready;

    // Idempotency: reuse existing subscription if present.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // Cast: ArrayBuffer subtype compatibility — safe for VAPID keys.
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as ArrayBuffer,
      });
    }

    const json = subscription.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;

    if (!endpoint || !p256dh || !auth) {
      return;
    }

    await registerPushSubscription({
      endpoint,
      keys: { p256dh, auth },
      userAgent: navigator.userAgent,
    });
  }, []);

  const requestPermission = useCallback(async () => {
    if (!vapidKey) return;
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    // Guard against concurrent calls.
    if (subscribing.current) return;
    subscribing.current = true;

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission as PushPermissionStatus);

      if (permission === 'granted') {
        await subscribe();
      }
    } finally {
      subscribing.current = false;
    }
  }, [subscribe]);

  return { permissionStatus, requestPermission };
}
